import React, { useEffect, useMemo, useState } from 'react';
import {
  PROGRAM_SET_NUMBERS,
  buildPreseasonPlan,
  makeProgramSetStatusRecord,
  preseasonOrderCsvRows,
  programSetIsActive,
  programSetName,
  programSetTargetIds,
  targetBuildTicket,
} from './preseasonPlanningModel';
import './preseason-planner.css';

function defaultSeasonYear() {
  const now = new Date();
  return now.getMonth() <= 5 ? now.getFullYear() : now.getFullYear() + 1;
}

function csvCell(value) {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadCsv(filename, rows) {
  if (!rows.length || typeof window === 'undefined') return;
  const headers = Object.keys(rows[0]);
  const csv = [headers, ...rows.map(row => headers.map(header => row[header]))]
    .map(row => row.map(csvCell).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export default function PreseasonPlanner() {
  const [data, setData] = useState({ catalog: [], expirations: [], settings: {} });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [seasonYear, setSeasonYear] = useState(defaultSeasonYear());
  const [savingSet, setSavingSet] = useState(0);
  const [selectedTargetId, setSelectedTargetId] = useState('LM-PACK-01');
  const [orderQuantities, setOrderQuantities] = useState({});

  const load = async () => {
    setStatus('loading');
    setError('');
    try {
      const response = await fetch('/api/tracking?admin=inventory', { cache: 'no-store' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to load preseason inventory.');
      setData(payload);
      setOrderQuantities({});
      setStatus('ready');
    } catch (err) {
      setError(err?.message || 'Unable to load preseason inventory.');
      setStatus('error');
    }
  };

  useEffect(() => { load(); }, []);

  const plan = useMemo(() => buildPreseasonPlan(data.catalog, data.expirations), [data.catalog, data.expirations]);
  const activeTargetIds = useMemo(() => new Set(plan.targetPlans.map(target => target.id)), [plan.targetPlans]);

  useEffect(() => {
    setOrderQuantities(current => {
      const next = { ...current };
      plan.orderRows.forEach(row => {
        if (next[row.catalogId] === undefined) next[row.catalogId] = row.needed;
      });
      Object.keys(next).forEach(id => {
        if (!plan.orderRows.some(row => row.catalogId === id)) delete next[id];
      });
      return next;
    });
  }, [plan.orderRows]);

  useEffect(() => {
    if (activeTargetIds.has(selectedTargetId)) return;
    setSelectedTargetId(plan.targetPlans[0]?.id || '');
  }, [activeTargetIds, plan.targetPlans, selectedTargetId]);

  const ticket = useMemo(() => targetBuildTicket(plan, selectedTargetId), [plan, selectedTargetId]);

  const postRecords = async records => {
    const response = await fetch('/api/tracking?admin=inventory', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ records }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || 'Unable to save preseason settings.');
    setData(payload);
    return payload;
  };

  const toggleSet = async number => {
    const currentlyActive = programSetIsActive(data.expirations, number);
    setSavingSet(number);
    setError('');
    setMessage('');
    try {
      await postRecords([makeProgramSetStatusRecord(number, !currentlyActive)]);
      setOrderQuantities({});
      setMessage(`${programSetName(number)} is now ${currentlyActive ? 'OUT OF SERVICE' : 'IN SERVICE'}. Master order totals and build tickets were recalculated.`);
    } catch (err) {
      setError(err?.message || 'Unable to change set status.');
    } finally {
      setSavingSet(0);
    }
  };

  const exportOrder = () => {
    const rows = preseasonOrderCsvRows(plan, orderQuantities);
    if (!rows.length) return setMessage('There is currently nothing to order for the active program sets.');
    downloadCsv(`line-medic-preseason-order-${seasonYear}.csv`, rows);
    setMessage(`Exported ${seasonYear} preseason Logistics order with ${rows.length} line items.`);
  };

  const plannedOrderUnits = plan.orderRows.reduce(
    (sum, row) => sum + Math.max(0, Number(orderQuantities[row.catalogId] ?? row.needed) || 0),
    0,
  );

  return (
    <div className="inventory-root">
      <header className="inventory-topbar">
        <div>
          <h1>Line Medic Preseason Planner</h1>
          <p>Choose the sets going in service, generate the Logistics order, then build each physical bag from the same plan</p>
        </div>
        <span>Private admin</span>
      </header>

      <main className="inventory-page preseason-page">
        {error && <div className="inventory-error">{error}</div>}
        {message && <div className="inventory-scan-success">{message}</div>}

        <section className="inventory-card">
          <div className="inventory-section-head">
            <div>
              <h2>1. Choose the program sets going in service</h2>
              <p>Each numbered set controls its Line Medic Pack, Trauma Bag, and O2 Bag together. Sets 01–03 default active; 04–06 default OOS until you activate them.</p>
            </div>
            <label className="preseason-year">Season
              <input type="number" min="2026" max="2100" value={seasonYear} onChange={event => setSeasonYear(Number(event.target.value) || defaultSeasonYear())} />
            </label>
          </div>
          <div className="preseason-set-grid">
            {PROGRAM_SET_NUMBERS.map(number => {
              const active = programSetIsActive(data.expirations, number);
              return (
                <button
                  type="button"
                  key={number}
                  className={`preseason-set-toggle${active ? ' active' : ''}`}
                  onClick={() => toggleSet(number)}
                  disabled={status !== 'ready' || Boolean(savingSet)}
                >
                  <span><strong>{programSetName(number)}</strong><small>{programSetTargetIds(number).join(' · ')}</small></span>
                  <strong>{savingSet === number ? 'SAVING…' : active ? 'IN SERVICE' : 'OUT OF SERVICE'}</strong>
                </button>
              );
            })}
          </div>
        </section>

        {status === 'loading' && <section className="inventory-card">Loading inventory and standards…</section>}

        {status === 'ready' && (
          <>
            <div className="inventory-summary preseason-summary">
              <button type="button"><small>Sets in service</small><strong>{plan.activeSets.length}</strong></button>
              <button type="button"><small>System need</small><strong>{plan.totalOrderUnits}</strong></button>
              <button type="button"><small>Planned order qty</small><strong>{plannedOrderUnits}</strong></button>
              <button type="button" className={plan.unresolved.length ? 'active warning' : ''}><small>Standards unresolved</small><strong>{plan.unresolved.length}</strong></button>
            </div>

            {plan.unresolved.length > 0 && (
              <section className="inventory-card preseason-warning-card">
                <h2>Standards still need a number</h2>
                <p>These catalog rows do not have a usable required quantity, so the planner will not invent an order quantity for them.</p>
                {[...new Map(plan.unresolved.map(row => [row.catalogId, row])).values()].map(row => (
                  <div key={row.catalogId}><strong>{row.item}</strong><span>{row.catalogId} · saved standard “{row.requiredLabel || 'blank'}”</span></div>
                ))}
              </section>
            )}

            <section className="inventory-card preseason-order-card">
              <div className="inventory-section-head">
                <div>
                  <h2>2. Master Logistics order</h2>
                  <p>System Need is the sum of each active physical container’s shortage. Order Qty is editable for case-pack or ordering realities without changing the pack standard.</p>
                </div>
                <div className="preseason-order-actions">
                  <button type="button" onClick={load}>Refresh</button>
                  <button type="button" className="inventory-save" onClick={exportOrder}>Export CSV</button>
                </div>
              </div>
              {!plan.orderRows.length && <div className="inventory-empty">No shortages are currently calculated for the active sets.</div>}
              {plan.orderRows.map(row => (
                <div className="preseason-order-row" key={row.catalogId}>
                  <span><strong>{row.item}</strong><small>{row.component} · {row.itemType || 'Unclassified'} · {row.catalogId}</small></span>
                  <span><small>Program required</small><strong>{row.required}</strong></span>
                  <span><small>Good stock assigned</small><strong>{row.onHand}</strong></span>
                  <span><small>System need</small><strong>{row.needed}</strong></span>
                  <label>Order Qty
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={orderQuantities[row.catalogId] ?? row.needed}
                      onChange={event => setOrderQuantities(current => ({ ...current, [row.catalogId]: Math.max(0, Number(event.target.value) || 0) }))}
                    />
                  </label>
                </div>
              ))}
            </section>

            <section className="inventory-card preseason-ticket-card">
              <div className="inventory-section-head">
                <div>
                  <h2>3. Individual build / pick ticket</h2>
                  <p>After the big order arrives, choose a physical container and pull exactly what that bag still needs from the staging pile.</p>
                </div>
                <label>Container
                  <select value={selectedTargetId} onChange={event => setSelectedTargetId(event.target.value)}>
                    {plan.targetPlans.map(target => <option key={target.id} value={target.id}>{target.name} — {target.id}</option>)}
                  </select>
                </label>
              </div>

              {!ticket && <div className="inventory-empty">Activate at least one program set to generate build tickets.</div>}
              {ticket && (
                <>
                  <div className={`preseason-ticket-status${ticket.neededUnits === 0 && !ticket.unresolved.length ? ' ready' : ''}`}>
                    <div><strong>{ticket.name}</strong><span>{ticket.id}</span></div>
                    <strong>{ticket.neededUnits === 0 && !ticket.unresolved.length ? 'READY' : `${ticket.neededUnits} TO PULL`}</strong>
                  </div>
                  {!ticket.rows.length && <div className="inventory-empty">This container currently meets every saved standard.</div>}
                  {ticket.rows.map(row => (
                    <div className={`preseason-ticket-row${row.standardResolved ? '' : ' unresolved'}`} key={row.catalogId}>
                      <span><strong>{row.item}</strong><small>{row.component} · {row.catalogId}</small></span>
                      {row.standardResolved ? (
                        <span><strong>Pull {row.needed}</strong><small>{row.onHand}/{row.required} already assigned</small></span>
                      ) : (
                        <span><strong>STANDARD NEEDED</strong><small>Required qty: {row.requiredLabel || 'blank'}</small></span>
                      )}
                    </div>
                  ))}
                </>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
